import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

import { applyWarehouseFilter, warehouseParamsSerializer } from '../utils/warehouseParams';

// Create axios instance for taxes API calls
const taxesAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: warehouseParamsSerializer,
});

// Add request interceptor to include auth token
taxesAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No auth token found in storage for taxesAPI request');
    }
    return applyWarehouseFilter(config);
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
taxesAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');

      // Redirect to login page
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const BASE_URL = '/taxes';

// Simple in-memory cache with optional sessionStorage persistence
const CACHE_PREFIX = 'erp_taxes_cache_v1';
const DEFAULT_TTL_MS = 10 * 60 * 1000; // 10 minutes

function cacheKey(key) {
  return `${CACHE_PREFIX}::${key}`;
}

function readCache(key) {
  try {
    const raw = sessionStorage.getItem(cacheKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.expires) return null;
    if (Date.now() > parsed.expires) {
      sessionStorage.removeItem(cacheKey(key));
      return null;
    }
    return parsed.value;
  } catch (err) {
    // If sessionStorage is not available (e.g. privacy mode), ignore and fallback to network
    return null;
  }
}

function writeCache(key, value, ttl = DEFAULT_TTL_MS) {
  try {
    const payload = JSON.stringify({ value, expires: Date.now() + ttl });
    sessionStorage.setItem(cacheKey(key), payload);
  } catch (err) {
    // ignore storage errors
  }
}

// Lightweight in-memory fallback (useful within single-page session)
const memoryCache = new Map();

function readMemoryCache(key) {
  const entry = memoryCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expires) {
    memoryCache.delete(key);
    return null;
  }
  return entry.value;
}

function writeMemoryCache(key, value, ttl = DEFAULT_TTL_MS) {
  memoryCache.set(key, { value, expires: Date.now() + ttl });
}

function deleteCacheEntry(key) {
  try {
    memoryCache.delete(key);
  } catch (e) {
    // ignore
  }
  try {
    sessionStorage.removeItem(cacheKey(key));
  } catch (e) {
    // ignore
  }
}

/**
 * Apply a modifier function to every cached full-list (all::...) entry.
 * The modifier receives a copy of the array and should return the new array.
 */
function updateFullListCachesWithModifier(modifierFn) {
  // Update memory cache entries
  try {
    for (const [key, entry] of memoryCache.entries()) {
      if (typeof key === 'string' && key.startsWith('all::')) {
        const arr = entry?.value;
        if (Array.isArray(arr)) {
          const updated = modifierFn(arr.slice());
          writeMemoryCache(key, updated);
        }
      }
    }
  } catch (e) {
    // ignore
  }

  // Update sessionStorage entries
  try {
    for (let i = 0; i < sessionStorage.length; i++) {
      const sk = sessionStorage.key(i);
      if (!sk) continue;
      if (sk.startsWith(`${CACHE_PREFIX}::all::`)) {
        const raw = sessionStorage.getItem(sk);
        if (!raw) continue;
        try {
          const parsed = JSON.parse(raw);
          const val = parsed?.value;
          if (Array.isArray(val)) {
            parsed.value = modifierFn(val.slice());
            sessionStorage.setItem(sk, JSON.stringify(parsed));
          }
        } catch (e) {
          // ignore malformed entry
        }
      }
    }
  } catch (e) {
    // ignore sessionStorage issues
  }
}


/**
 * Taxes Service - Handles all API calls related to tax management
 * - Exposes helpers for list, get, create, update and delete operations
 */
const taxesService = {
  /**
   * Get all taxes with optional filters
   * @param {Object} params - Query parameters
   * @param {number} params.skip - Number of records to skip (pagination)
   * @param {number} params.limit - Number of records to return
   * @param {string} params.search - Search term
   * @param {number} params.gst_rate - Filter by GST rate
   * @param {number} params.min_rate - Minimum rate filter
   * @param {number} params.max_rate - Maximum rate filter
   * @returns {Promise} API response with taxes data
   */
  async getAllTaxes(params = {}) {
    const key = `all::${JSON.stringify(params || {})}`;
    // Try memory cache first
    const mem = readMemoryCache(key);
    if (mem) return { success: true, data: mem };

    // Then sessionStorage cache
    const sess = readCache(key);
    if (sess) {
      // populate memory cache for faster subsequent reads
      writeMemoryCache(key, sess);
      return { success: true, data: sess };
    }

    try {
      const queryParams = new URLSearchParams();
      if (params.skip !== undefined) queryParams.append('skip', params.skip);
      if (params.limit !== undefined) queryParams.append('limit', params.limit);
      const qs = queryParams.toString();
      const response = await taxesAPI.get(qs ? `${BASE_URL}?${qs}` : BASE_URL);
      const data = response.data;
      // cache results
      writeMemoryCache(key, data);
      writeCache(key, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Get a specific tax by ID
   * @param {number|string} taxId - Tax ID
   * @returns {Promise} API response with tax data
   */
  async getTaxById(taxId) {
    const key = `byId::${taxId}`;
    const mem = readMemoryCache(key);
    if (mem) return { success: true, data: mem };

    const sess = readCache(key);
    if (sess) {
      writeMemoryCache(key, sess);
      return { success: true, data: sess };
    }

    // As a fallback, try to find the tax in a cached full-list
    const fullKey = `all::${JSON.stringify({})}`;
    const fullMem = readMemoryCache(fullKey) || readCache(fullKey);
    if (fullMem && Array.isArray(fullMem)) {
      const found = fullMem.find((t) => String(t.id) === String(taxId) || String(t.tax_id) === String(taxId));
      if (found) {
        writeMemoryCache(key, found);
        writeCache(key, found);
        return { success: true, data: found };
      }
    }

    try {
      const response = await taxesAPI.get(`${BASE_URL}/${taxId}`);
      const data = response.data;
      writeMemoryCache(key, data);
      writeCache(key, data);
      return {
        success: true,
        data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Create a new tax rate
   * @param {Object} taxData - Tax data
   * @param {number} taxData.gst - GST rate
   * @param {number} taxData.cgst - CGST rate
   * @param {number} taxData.sgst - SGST rate
   * @param {number} taxData.igst - IGST rate
   * @returns {Promise} API response with created tax data
   */
  async createTax(taxData) {
    try {
      const response = await taxesAPI.post(BASE_URL, taxData);
      const created = response.data;

      // Update cached full-lists by prepending the created tax
      updateFullListCachesWithModifier((arr) => {
        // Avoid duplicates
        const exists = arr.some((t) => String(t.id || t.tax_id) === String(created.id || created.tax_id));
        if (exists) return arr;
        return [created, ...arr];
      });

      // Also cache item by id
      try {
        const key = `byId::${created.id || created.tax_id}`;
        writeMemoryCache(key, created);
        writeCache(key, created);
      } catch (e) { }

      return {
        success: true,
        data: created,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Update an existing tax rate
   * @param {number|string} taxId - Tax ID
   * @param {Object} taxData - Updated tax data
   * @returns {Promise} API response with updated tax data
   */
  async updateTax(taxId, taxData) {
    try {
      const response = await taxesAPI.put(`${BASE_URL}/${taxId}`, taxData);
      const updated = response.data;

      // Update cached full-lists by replacing the item
      updateFullListCachesWithModifier((arr) => {
        return arr.map((t) => (String(t.id || t.tax_id) === String(taxId) ? updated : t));
      });

      // Update byId cache
      try {
        const key = `byId::${taxId}`;
        writeMemoryCache(key, updated);
        writeCache(key, updated);
      } catch (e) { }

      return {
        success: true,
        data: updated,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Delete a tax rate
   * @param {number|string} taxId - Tax ID
   * @returns {Promise} API response
   */
  async deleteTax(taxId) {
    try {
      const response = await taxesAPI.delete(`${BASE_URL}/${taxId}`);

      // Remove from cached full-lists
      updateFullListCachesWithModifier((arr) => arr.filter((t) => String(t.id || t.tax_id) !== String(taxId)));

      // Remove byId cache
      try {
        deleteCacheEntry(`byId::${taxId}`);
      } catch (e) { }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },

  /**
   * Delete multiple taxes
   * @param {Array<number|string>} taxIds - Array of tax IDs
   * @returns {Promise} API response
   */
  async deleteTaxes(taxIds) {
    try {
      // POST /taxes/bulk-delete — there is no DELETE /taxes route; the old call 404'd
      // silently. The backend skips taxes still assigned to an HSN code (returned in
      // `errors`) instead of failing the whole batch.
      const response = await taxesAPI.post(`${BASE_URL}/bulk-delete`, { ids: taxIds });

      // Remove deleted ids from full-list caches
      const idSet = new Set((taxIds || []).map((id) => String(id)));
      updateFullListCachesWithModifier((arr) => arr.filter((t) => !idSet.has(String(t.id || t.tax_id))));

      // Remove byId cache entries
      try {
        for (const id of taxIds) deleteCacheEntry(`byId::${id}`);
      } catch (e) { }

      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return {
        success: false,
        error: extractApiError(error),
      };
    }
  },
};

export default taxesService;